import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLogout } from '../../hooks/useAuth';

/**
 * User Avatar Menu component for header.
 * Sprint 31.9 Phase 11: Identity Center header integration.
 * 
 * Features:
 * - 34px circular avatar with user's first initial
 * - Dropdown menu with: Edit Profile, Manage API Tokens, Switch Session, Log Out
 * - Switch Session only shown when multiple sessions available
 */
export function UserAvatarMenu() {
  const { user, session, availableSessions } = useAuthStore();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Get user's initials for avatar
  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return '?';
  };

  const handleMenuItemClick = (action: string) => {
    setIsOpen(false);
    switch (action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'tokens':
        navigate('/manage/tokens');
        break;
      case 'switch':
        // Return to session selection page without full logout
        useAuthStore.getState().returnToSessionSelect();
        navigate('/select-session');
        break;
      case 'logout':
        logout();
        break;
    }
  };

  const showSwitchSession = availableSessions.length > 1;

  return (
    <div className="user-avatar-menu" ref={menuRef} style={{ position: 'relative' }}>
      {/* Avatar button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="avatar-button"
        aria-label="User menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
        style={{
          width: '34px',
          height: '34px',
          borderRadius: '50%',
          border: 'none',
          background: 'var(--color-primary)',
          color: 'white',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {getInitials()}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="avatar-dropdown"
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: '200px',
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {/* User info header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--color-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}`
                : user?.email}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              marginTop: '2px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {user?.email}
            </div>
          </div>

          {/* Menu items */}
          <div style={{ padding: '4px 0' }}>
            <MenuItem
              icon={<ProfileIcon />}
              label="Edit Profile"
              onClick={() => handleMenuItemClick('profile')}
            />
            <MenuItem
              icon={<TokenIcon />}
              label="Manage API Tokens"
              onClick={() => handleMenuItemClick('tokens')}
            />
            {showSwitchSession && (
              <MenuItem
                icon={<SwitchIcon />}
                label="Switch Session"
                onClick={() => handleMenuItemClick('switch')}
                badge={session?.tenantName}
              />
            )}
          </div>

          {/* Logout */}
          <div style={{ 
            padding: '4px 0', 
            borderTop: '1px solid var(--color-border)',
          }}>
            <MenuItem
              icon={<LogoutIcon />}
              label={isLoggingOut ? 'Logging out...' : 'Log Out'}
              onClick={() => handleMenuItemClick('logout')}
              disabled={isLoggingOut}
              destructive
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Menu item component
 */
function MenuItem({ 
  icon, 
  label, 
  onClick, 
  badge,
  disabled,
  destructive,
}: { 
  icon: React.ReactNode; 
  label: string; 
  onClick: () => void;
  badge?: string | undefined;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 16px',
        border: 'none',
        background: 'transparent',
        color: destructive ? 'var(--color-danger)' : 'var(--color-text)',
        fontSize: '14px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        textAlign: 'left',
        transition: 'background 0.1s ease',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'var(--color-bg-tertiary)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span style={{ 
        width: '16px', 
        height: '16px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: destructive ? 'var(--color-danger)' : 'var(--color-text-secondary)',
      }}>
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge && (
        <span style={{
          fontSize: '11px',
          padding: '2px 6px',
          borderRadius: '4px',
          background: 'var(--color-bg-tertiary)',
          color: 'var(--color-text-secondary)',
        }}>
          {badge}
        </span>
      )}
    </button>
  );
}

// Icon components
function ProfileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 8a3 3 0 100-6 3 3 0 000 6zM2 14s-1 0-1-1 1-4 7-4 7 3 7 4-1 1-1 1H2z"/>
    </svg>
  );
}

function TokenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  );
}

function SwitchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path fillRule="evenodd" d="M1 11.5a.5.5 0 00.5.5h11.793l-3.147 3.146a.5.5 0 00.708.708l4-4a.5.5 0 000-.708l-4-4a.5.5 0 00-.708.708L13.293 11H1.5a.5.5 0 00-.5.5zm14-7a.5.5 0 01-.5.5H2.707l3.147 3.146a.5.5 0 11-.708.708l-4-4a.5.5 0 010-.708l4-4a.5.5 0 11.708.708L2.707 4H14.5a.5.5 0 01.5.5z"/>
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path fillRule="evenodd" d="M10 12.5a.5.5 0 01-.5.5h-8a.5.5 0 01-.5-.5v-9a.5.5 0 01.5-.5h8a.5.5 0 01.5.5v2a.5.5 0 001 0v-2A1.5 1.5 0 009.5 2h-8A1.5 1.5 0 000 3.5v9A1.5 1.5 0 001.5 14h8a1.5 1.5 0 001.5-1.5v-2a.5.5 0 00-1 0v2z"/>
      <path fillRule="evenodd" d="M15.854 8.354a.5.5 0 000-.708l-3-3a.5.5 0 00-.708.708L14.293 7.5H5.5a.5.5 0 000 1h8.793l-2.147 2.146a.5.5 0 00.708.708l3-3z"/>
    </svg>
  );
}
